import { format, isWithinInterval, parseISO, differenceInDays } from "date-fns";
import { DateRange } from "react-day-picker";

// Types for our metrics data
export interface MetricData {
  date: string;
  value: number;
}

export interface Referrer {
  id: string;
  name: string;
  email: string;
  referrals: number;
  conversions: number;
  lastActive: string;
}

export interface FormattedDateRange {
  fromDate: string;
  toDate: string;
  daysDiff: number; // Changed to ensure this is a number type
}

export interface MetricsData {
  revenues: MetricData[];
  coupons: MetricData[];
  newCustomers: MetricData[];
}

export interface CalculatedMetrics {
  totalRevenue: number;
  totalCoupons: number;
  totalReferrals: number;
  totalConversions: number;
  conversionRate: number;
  averageTicket: number;
}

// Helper function to check if a date is within range
export const isWithinDateRange = (dateStr: string, fromDate: string, toDate: string) => {
  const date = parseISO(dateStr);
  return isWithinInterval(date, {
    start: parseISO(fromDate),
    end: parseISO(toDate),
  });
};

// Calculate optimal chunk size based on date range
export const calculateChunkSize = (fromDate: Date, toDate: Date): number => {
  const daysDiff = differenceInDays(toDate, fromDate);
  if (daysDiff <= 7) return daysDiff + 1; // Daily granularity for small ranges
  if (daysDiff <= 30) return 7; // Weekly chunks for medium ranges
  if (daysDiff <= 90) return 15; // Bi-weekly chunks for 1-3 months
  return 30; // Monthly chunks for larger ranges
};

// Format chunk key for caching
export const formatChunkKey = (date: Date, chunkSize: number): string => {
  return `${format(date, 'yyyy-MM-dd')}_${chunkSize}`;
};

// Format dates for display
export const formatDatesForDisplay = (data: any[]): any[] => {
  return data.map(d => ({
    ...d,
    date: format(parseISO(d.date), 'dd/MM')
  }));
};

// Format currency value
export const formatCurrency = (value: number): string => {
  return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};
